/**
 * faceCaptureCodec — compact encodings for the recording's `capture` block
 * (ENG-1107). Landmarks and GNM coefficients go as Int16 fixed-point (one
 * `scale` step per integer, clamped) and the few per-frame floats that need
 * range (transformation matrix, joint rotations) as raw Float32 — both as
 * base64 strings inside the face-clip JSON, so the file stays ONE JSON
 * document the existing FaceClipPlayer loads and ignores the extra key of.
 *
 * Own base64 (no Buffer) so the same module runs in the browser and under
 * node:test. Pure.
 */
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
const B64_REV = new Int16Array(256).fill(-1)
for (let i = 0; i < 64; i++) B64_REV[B64.charCodeAt(i)] = i

export function bytesToBase64(u8) {
  let out = ''
  let i = 0
  for (; i + 2 < u8.length; i += 3) {
    const n = (u8[i] << 16) | (u8[i + 1] << 8) | u8[i + 2]
    out += B64[n >> 18] + B64[(n >> 12) & 63] + B64[(n >> 6) & 63] + B64[n & 63]
  }
  if (i < u8.length) {
    const rem = u8.length - i
    const n = (u8[i] << 16) | (rem === 2 ? u8[i + 1] << 8 : 0)
    out += B64[n >> 18] + B64[(n >> 12) & 63] + (rem === 2 ? B64[(n >> 6) & 63] : '=') + '='
  }
  return out
}

export function base64ToBytes(str) {
  const clean = str.replace(/=+$/, '')
  const outLen = Math.floor((clean.length * 3) / 4)
  const out = new Uint8Array(outLen)
  let o = 0
  for (let i = 0; i < clean.length; i += 4) {
    const a = B64_REV[clean.charCodeAt(i)]
    const b = B64_REV[clean.charCodeAt(i + 1)]
    const c = i + 2 < clean.length ? B64_REV[clean.charCodeAt(i + 2)] : 0
    const d = i + 3 < clean.length ? B64_REV[clean.charCodeAt(i + 3)] : 0
    if (a < 0 || b < 0 || c < 0 || d < 0) throw new Error('[faceCaptureCodec] invalid base64')
    const n = (a << 18) | (b << 12) | (c << 6) | d
    if (o < outLen) out[o++] = (n >> 16) & 255
    if (o < outLen) out[o++] = (n >> 8) & 255
    if (o < outLen) out[o++] = n & 255
  }
  return out
}

/** Values -> Int16 little-endian, one `scale` per step, clamped. */
export function packInt16Base64(values, scale) {
  const n = values.length
  const i16 = new Int16Array(n)
  const inv = 1 / scale
  for (let i = 0; i < n; i++) {
    const q = Math.round(values[i] * inv)
    i16[i] = q > 32767 ? 32767 : q < -32768 ? -32768 : q
  }
  return bytesToBase64(new Uint8Array(i16.buffer))
}

export function unpackInt16Base64(str, scale) {
  const bytes = base64ToBytes(str)
  const i16 = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength >> 1)
  const out = new Float32Array(i16.length)
  for (let i = 0; i < i16.length; i++) out[i] = i16[i] * scale
  return out
}

export function packFloat32Base64(values) {
  const f32 = values instanceof Float32Array ? values : Float32Array.from(values)
  return bytesToBase64(new Uint8Array(f32.buffer, f32.byteOffset, f32.byteLength))
}

export function unpackFloat32Base64(str) {
  const bytes = base64ToBytes(str)
  // Copy into an aligned buffer: a Float32Array view needs 4-byte alignment.
  const aligned = new Uint8Array(bytes.length)
  aligned.set(bytes)
  return new Float32Array(aligned.buffer, 0, aligned.length >> 2)
}